import React, { useCallback, useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { showAlert } from 'actions/app';
import {
  setBulkEmailInPreview,
  setBulkEmailStatus,
  startDeletingBulkDraft,
  startDeletingScheduledBulkEmail,
  startReschedulingBulkEmail,
  startSendingNowScheduledBulkEmail,
  startSettingBulkPreview,
  startSettingSelectedTemplateData
} from 'actions/bulkEmail';
import { BulkEmailStepsDrawer } from 'features/bulk-email/sequences/components/BulkEmailStepsDrawer';
import {
  MAX_STEPS_NUMBER,
  SEQUENCE_STATUS_IDS,
  STEPS_ORDINAL_START
} from 'features/bulk-email/sequences/constants';
import { useBulkTemplates } from 'hooks/BulkEmails/templates';
import { useBulkQuotaRestriction } from 'hooks/bulkQuotaRestriction';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { Permissions, userHasPermission, userHasSomeRoles } from 'services/Authorization';
import { cancelSequence } from 'services/bulkEmail';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import BulkEmailContentHeader from 'UI/components/molecules/BulkEmailContentHeader';
import PreviewBulkEmail from 'UI/components/molecules/PreviewBulkEmail';
import BulkEmailContentBody from 'UI/components/organisms/BulkEmail/BulkEmailContentBody';
import BulkEmailsList from 'UI/components/organisms/BulkEmail/EmailsList';
import ScheduleDialog from 'UI/components/organisms/BulkEmail/ScheduleDialog';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import {
  BulkMenuItems,
  MY_TEMPLATES_FOLDER_ID,
  TEMPLATE_ENTITY_TYPES
} from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { SEQUENCE_PRIVILEGED_ROLES } from 'UI/constants/roles';
import { getId } from 'UI/utils';

import BulkStatsDrawer from '../../BulkStatsDrawer';
import BulkTemplatesDrawer from '../BulkEmailTemplates/BulkTemplatesDrawer';
import TemplateFolderDrawer from '../BulkEmailTemplates/TemplateFolderDrawer';
import TemplatePreview from '../BulkEmailTemplates/TemplatePreview';
import TemplateRestoreForm from '../BulkEmailTemplates/TemplateRestoreForm';
import TemplateTreeViewList from '../BulkEmailTemplates/TemplateTreeViewList';
import OptInOptOutList from '../OptInOptOutList';
import RestrictionDialog from '../RestrictionDialog/RestrictionDialog';

import { useStyles } from './styles';

const {
  content: {
    drafts: draftsCopies,
    schedule: scheduleCopies,
    sent: sentCopies,
    template: templateCopies
  },
  dialogs: dialogsCopies
} = strings.bulkEmails;

const BULK_DIALOGS = {
  cancelSequence: 'isSequenceDialogOpen',
  deleteDraft: 'isDeleteDraftDialogOpen',
  deleteSchedule: 'isDeleteScheduleDialogOpen',
  reschedule: 'isRescheduleDialogOpen',
  sendNow: 'isSendNowDialogOpen'
};

const ARCHIVE_DIALOG_TYPE_ID = 3;

/**
 * Bulk email content component for managing bulk email lists, drafts, schedules, and templates
 * @param {Object} props
 * @param {Object} props.BulkEmailsListProps - Props passed to the bulk emails list component
 * @param {boolean} [props.isResponsive] - Whether to use responsive layout
 * @param {Function} [props.onAddEmailToOptOut=() => {}] - Callback to add email to opt-out list
 * @param {Function} [props.onKeywordSearch] - Callback for keyword search
 * @param {Function} [props.onOpenBulkForm] - Callback to open the bulk email form
 */
const BulkEmailContent = ({
  BulkEmailsListProps,
  isResponsive,
  onAddEmailToOptOut = () => {},
  onKeywordSearch,
  onOpenBulkForm
}) => {
  const classes = useStyles();
  const [currentUser] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const areBulkSequencesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailSequences);
  const isBulkEmailAIEnabled =
    checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailAI) &&
    userHasPermission(Permissions.BulkEmail.AIBuilder);

  const {
    foldersIdToRefresh,
    getTemplatesActions,
    templateHandlers,
    templatesTreeItem,
    templatesUiState,
    templateTogglers,
    templateArchiveDialogProps,
    selectedTreeItemId
  } = useBulkTemplates();

  const isAllowedToCreate =
    templatesTreeItem.belongsToUser &&
    templatesTreeItem?.id !== MY_TEMPLATES_FOLDER_ID &&
    !templatesTreeItem?.isArchived &&
    templatesTreeItem?.folderTypeId !== ARCHIVE_DIALOG_TYPE_ID;

  const [uiState, setUiState] = useState({
    [BULK_DIALOGS.cancelSequence]: false,
    [BULK_DIALOGS.deleteDraft]: false,
    [BULK_DIALOGS.deleteSchedule]: false,
    [BULK_DIALOGS.reschedule]: false,
    [BULK_DIALOGS.sendNow]: false,
    singleStepDrawer: {
      open: false,
      step: null
    },
    isStatsDrawerOpen: false
  });

  const form = useForm();
  const { bulkEmailInPreview } = useSelector(store => store.bulkEmail.domain);
  const {
    id: bulkEmailPreviewId,
    searchProject: searchProjectInPreview,
    userInfo
  } = bulkEmailInPreview.data || {};
  const {
    preview: { loading: loadingBulkPreview },
    selectedMenuItem,
    status: bulkEmailStatus
  } = useSelector(store => store.bulkEmail.ui);

  const dispatch = useDispatch();

  const bulkScope = bulkEmailInPreview?.data?.emailConfig?.bulkType;
  const shouldShowHeaderActions = currentUser.id === userInfo?.id;
  const isBulkPreviewActive = bulkEmailPreviewId && bulkEmailInPreview?.data?.emailBody;
  const bulkRestrictionRule = bulkEmailStatus.bulkRestriction?.rule;
  const searchProjectSize = searchProjectInPreview?.bulkableItems;
  const shouldShowSequencePreview = areBulkSequencesEnabled && bulkEmailInPreview.data?.sequences;
  const sequence = bulkEmailInPreview?.data?.sequences;
  const activeSteps = sequence?.entries?.filter(
    ({ statusId }) => statusId !== SEQUENCE_STATUS_IDS.canceled
  );
  const isSequenceCanceled = sequence?.statusId === SEQUENCE_STATUS_IDS.canceled;
  const isSequenceCompleted = sequence?.statusId === SEQUENCE_STATUS_IDS.completed;
  const sequenceHasMaxSteps = activeSteps?.length >= MAX_STEPS_NUMBER;
  const isCurrentUserBulkOwner = currentUser?.id === userInfo?.id;
  const shouldShowCancelSequenceActions =
    isCurrentUserBulkOwner || userHasSomeRoles(currentUser, SEQUENCE_PRIVILEGED_ROLES);

  const toggleSequenceStepsDrawer =
    ({ open }) =>
    () => {
      setUiState(prev => ({
        ...prev,
        singleStepDrawer: {
          open,
          step: open
            ? { id: getId(), currentStep: (activeSteps?.length || 0) + STEPS_ORDINAL_START }
            : null
        }
      }));
    };

  const { bulkQuota, bulkQuotaRestriction, notifyBulkQuotaRestriction } = useBulkQuotaRestriction({
    searchProjectSize,
    shouldSchedule: uiState[BULK_DIALOGS.reschedule]
  });

  // TODO: extract schedule dialogs to its own component
  const handleToggleBulkDialogs = useCallback(
    /**
     * @param {boolean} open
     * @param {string} key
     * @returns {Function}
     */
    (open, key) => () => {
      dispatch(setBulkEmailInPreview({ edit: open }));
      setUiState(prev => ({ ...prev, [key]: open }));
    },
    [dispatch]
  );

  const openSendNowConfirmDialog = () => {
    dispatch(setBulkEmailInPreview({ edit: true }));
    dispatch(setBulkRestrictionToInitial());
    setUiState(prev => ({ ...prev, isSendNowDialogOpen: true }));
  };

  useEffect(() => {
    if (bulkRestrictionRule) {
      setUiState(prev => ({
        ...prev,
        isRescheduleDialogOpen: false,
        isSendNowDialogOpen: false
      }));
    }
  }, [bulkRestrictionRule]);

  useEffect(() => {
    if (bulkEmailStatus.completed && !bulkEmailStatus.loading && !bulkEmailInPreview.edit) {
      dispatch(setBulkEmailStatus({ completed: false }));
      uiState.isSendNowDialogOpen && handleToggleBulkDialogs(false, BULK_DIALOGS.sendNow)();
      uiState.isRescheduleDialogOpen && handleToggleBulkDialogs(false, BULK_DIALOGS.reschedule)();
      uiState.isDeleteScheduleDialogOpen &&
        handleToggleBulkDialogs(false, BULK_DIALOGS.deleteSchedule)();
      uiState.isDeleteDraftDialogOpen && handleToggleBulkDialogs(false, BULK_DIALOGS.deleteDraft)();
    }
  }, [
    bulkEmailInPreview.edit,
    bulkEmailStatus.completed,
    bulkEmailStatus.loading,
    dispatch,
    handleToggleBulkDialogs,
    uiState
  ]);

  const toggleStatsDrawer = open => () =>
    setUiState(prev => ({ ...prev, isStatsDrawerOpen: open }));
  const handleDeleteSchedule = canDeleteSchedule =>
    canDeleteSchedule && dispatch(startDeletingScheduledBulkEmail(bulkEmailPreviewId));
  const handleRescheduleBulk = formData =>
    dispatch(
      startReschedulingBulkEmail(bulkEmailPreviewId, {
        data: formData
      })
    );
  const handleSendNowSchedule = canSendScheduleNow =>
    canSendScheduleNow &&
    dispatch(
      startSendingNowScheduledBulkEmail(
        { bulk_id: bulkEmailPreviewId },
        { sendNowScheduleBulk: true, isBulkEmailAIEnabled }
      )
    );
  const handleDeleteBulkDraft = canDeleteBulkDraft =>
    canDeleteBulkDraft && dispatch(startDeletingBulkDraft(bulkEmailPreviewId));

  const handleKeywordSearch = search => onKeywordSearch && onKeywordSearch(search);

  const setBulkFormPreloadState = () => {
    const { emailConfig: config, emailBody: body } = bulkEmailInPreview.data;
    dispatch(
      startSettingSelectedTemplateData({
        id: config.template?.id,
        data: {
          name: config.template?.name,
          emailBody: {
            subject: body?.subject,
            html: body?.html,
            text: body?.text,
            attachments: body?.attachments
          }
        }
      })
    );
    dispatch(setBulkEmailInPreview({ edit: true }));
    onOpenBulkForm();
  };

  const handleSelectEmail = selectedMailId =>
    bulkEmailPreviewId !== selectedMailId && dispatch(startSettingBulkPreview(selectedMailId));

  const emailsListsContentBodyProps = {
    loadingPreview: loadingBulkPreview,
    loadingList: BulkEmailsListProps.loading,
    isListWithContent: BulkEmailsListProps.emailsList.length > 0,
    isPreviewActive: isBulkPreviewActive,
    ListComponent: (
      <BulkEmailsList
        emailInPreviewId={bulkEmailPreviewId}
        onSelectEmail={handleSelectEmail}
        selectedMenuItem={selectedMenuItem}
        {...BulkEmailsListProps}
      />
    ),
    PreviewComponent: (
      <PreviewBulkEmail
        key={bulkEmailPreviewId || 'no-preview'}
        bulkInPreviewData={bulkEmailInPreview?.data}
        selectedMenuItem={selectedMenuItem}
        shouldReloadEmailList={bulkEmailStatus.reloadEmailList}
      />
    )
  };

  const sendNowHandler = () => {
    if (bulkQuotaRestriction) {
      notifyBulkQuotaRestriction();
      return;
    }
    openSendNowConfirmDialog();
  };

  const containerProps = {
    [BulkMenuItems.Sent]: {
      ContentHeaderProps: shouldShowSequencePreview
        ? {
            buttonsDefinitions: [
              {
                type: 'action',
                props: {
                  disabled: loadingBulkPreview || isSequenceCanceled || isSequenceCompleted,
                  onClick: handleToggleBulkDialogs(true, BULK_DIALOGS.cancelSequence),
                  size: 'small',
                  text: sentCopies.sequences.buttons.cancelSequence,
                  variant: 'outlined'
                },
                visible: shouldShowCancelSequenceActions
              },
              {
                type: 'action',
                props: {
                  disabled:
                    loadingBulkPreview ||
                    isSequenceCanceled ||
                    isSequenceCompleted ||
                    sequenceHasMaxSteps,
                  onClick: toggleSequenceStepsDrawer({ open: true }),
                  size: 'small',
                  text: sentCopies.sequences.buttons.addSteps
                },
                visible: isCurrentUserBulkOwner
              }
            ],
            onChange: handleKeywordSearch,
            title: sentCopies.sequences.title
          }
        : {
            buttonsDefinitions: [
              {
                type: 'action',
                props: {
                  disabled: !bulkEmailInPreview?.data?.createdAt,
                  onClick: toggleStatsDrawer(true),
                  size: 'small',
                  text: sentCopies.bulkStatsButton
                }
              }
            ],
            onChange: handleKeywordSearch,
            title: sentCopies.title
          },
      ContentBodyProps: emailsListsContentBodyProps
    },
    [BulkMenuItems.Drafts]: {
      ContentHeaderProps: {
        buttonsDefinitions: shouldShowHeaderActions
          ? [
              {
                type: 'menu',
                props: {
                  isIconButton: true,
                  menuItems: [
                    {
                      title: draftsCopies.menuItemsTitles.edit,
                      action: setBulkFormPreloadState,
                      visible: true
                    },
                    {
                      title: draftsCopies.menuItemsTitles.delete,
                      action: handleToggleBulkDialogs(true, BULK_DIALOGS.deleteDraft),
                      visible: true
                    }
                  ]
                }
              }
            ]
          : null,
        onChange: handleKeywordSearch,
        title: draftsCopies.title
      },
      ContentBodyProps: emailsListsContentBodyProps
    },
    [BulkMenuItems.Schedule]: {
      ContentHeaderProps: {
        buttonsDefinitions: shouldShowHeaderActions
          ? [
              {
                type: 'menu',
                props: {
                  isIconButton: true,
                  menuItems: [
                    {
                      title: scheduleCopies.menuItemsTitles.edit,
                      action: setBulkFormPreloadState,
                      visible: true
                    },
                    {
                      title: scheduleCopies.menuItemsTitles.sendNow,
                      action: sendNowHandler,
                      visible: true
                    },
                    {
                      title: scheduleCopies.menuItemsTitles.reschedule,
                      action: handleToggleBulkDialogs(true, BULK_DIALOGS.reschedule),
                      visible: true
                    },
                    {
                      title: scheduleCopies.menuItemsTitles.delete,
                      action: handleToggleBulkDialogs(true, BULK_DIALOGS.deleteSchedule),
                      visible: true
                    }
                  ]
                }
              }
            ]
          : null,
        onChange: handleKeywordSearch,
        title: scheduleCopies.title
      },
      ContentBodyProps: emailsListsContentBodyProps
    },
    [BulkMenuItems.Template]: {
      ContentHeaderProps: {
        onSelect: templateHandlers.selectTreeItem,
        searchbarPlaceHolder: templateCopies.searchBarPlaceholder,
        title:
          Object.keys(templatesTreeItem).length > 0
            ? templatesTreeItem?.name
            : templateCopies.title,
        ...getTemplatesActions()
      },
      ContentBodyProps: {
        loadingList: false,
        loadingPreview: templatesUiState.loadingPreview,
        isListWithContent: true,
        isPreviewActive:
          templatesTreeItem.id && templatesTreeItem.entity === TEMPLATE_ENTITY_TYPES.Template,
        ListComponent: (
          <TemplateTreeViewList
            TemplatesTreeViewProps={{
              onClickItem: templateHandlers.selectTreeItem,
              onFolderRefresh: templateHandlers.folderRefresh,
              foldersIdToRefresh,
              selectedTreeItemId
            }}
            isAllowedToCreate={isAllowedToCreate}
            onOpenFolderDrawer={templateTogglers.folderDrawer({ open: true, edit: false })}
            onOpenTemplateDrawer={templateTogglers.templateDrawer({ open: true, edit: false })}
          />
        ),
        PreviewComponent: <TemplatePreview template={templatesTreeItem} />
      }
    }
  };

  const content = {
    [BulkMenuItems.OptOut]: <OptInOptOutList onAddEmailToOptOut={onAddEmailToOptOut} />
  };

  const scheduleSendNowDetails = [
    {
      title: 'Search Project Name',
      detail: `${bulkEmailInPreview?.data?.searchProject?.name} (${searchProjectSize})`
    },
    { title: 'Bulk Type', detail: bulkScope?.title },
    { title: 'Template', detail: bulkEmailInPreview?.data?.emailConfig?.template?.name },
    { title: 'Subject', detail: bulkEmailInPreview?.data?.emailBody?.subject }
  ];

  const setBulkRestrictionToInitial = () =>
    dispatch(setBulkEmailStatus({ bulkRestriction: { rule: null, reason: '' } }));

  const handelReschedule = () => {
    setBulkRestrictionToInitial();
    setUiState(prev => ({ ...prev, isRescheduleDialogOpen: true }));
  };

  const closeTemplateDrawer = () => {
    templateTogglers.templateDrawer({ open: false, edit: false })();
    templateTogglers.discardTemplateDialog({ open: false })();
  };

  const handleOnCloseTemplateDrawer = hasChanges =>
    hasChanges ? templateTogglers.discardTemplateDialog({ open: true })() : closeTemplateDrawer();

  const handleCancelSequence = async () => {
    dispatch(setBulkEmailStatus({ loading: true }));
    const { success, alert } = await cancelSequence(bulkEmailPreviewId);
    dispatch(showAlert(alert));
    dispatch(setBulkEmailStatus({ loading: false }));

    if (success) {
      handleToggleBulkDialogs(false, BULK_DIALOGS.cancelSequence)();
      dispatch(setBulkEmailStatus({ reloadEmailList: true }));
    }
  };

  return (
    content[selectedMenuItem] || (
      <>
        <BulkEmailContentHeader
          searchbarPlaceHolder={sentCopies.searchBarPlaceholder}
          {...containerProps[selectedMenuItem]?.ContentHeaderProps}
        />
        <Grid data-testid="bulk-lists-default-layout" container className={classes.listContainer}>
          <BulkEmailContentBody
            isResponsive={isResponsive}
            selectedMenuItem={selectedMenuItem}
            {...(containerProps[selectedMenuItem]?.ContentBodyProps || {})}
          />
        </Grid>
        <When condition={bulkRestrictionRule}>
          <RestrictionDialog
            onOpenScheduleDialog={handelReschedule}
            onClose={setBulkRestrictionToInitial}
            bulkRestriction={bulkEmailStatus.bulkRestriction}
            onSubmitBulkEmail={openSendNowConfirmDialog}
            isSendScheduleNow
          />
        </When>
        <When condition={uiState.isRescheduleDialogOpen}>
          <FormContext {...form}>
            <ScheduleDialog
              notifyBulkQuotaRestriction={notifyBulkQuotaRestriction}
              onClose={handleToggleBulkDialogs(false, BULK_DIALOGS.reschedule)}
              onSubmitBulkEmail={handleRescheduleBulk}
              quotaRestriction={bulkQuotaRestriction}
              remainingQuota={bulkQuota?.remainingQuota}
              shouldUnregisterFieldsOnClose
            />
          </FormContext>
        </When>
        <When condition={uiState.isDeleteScheduleDialogOpen}>
          <DecisionDialog
            {...dialogsCopies.scheduleDelete}
            fetching={bulkEmailStatus.loading}
            isOpened
            onClose={handleToggleBulkDialogs(false, BULK_DIALOGS.deleteSchedule)}
            onConfirm={handleDeleteSchedule}
            withButtons="YesNo"
          />
        </When>
        <When condition={uiState.isSendNowDialogOpen}>
          <DecisionDialog
            {...dialogsCopies.scheduleSendNow}
            closeDialogOnConfirm={false}
            mustComplete
            taskCompleted={bulkEmailStatus.completed}
            fetching={bulkEmailStatus.loading}
            onClose={handleToggleBulkDialogs(false, BULK_DIALOGS.sendNow)}
            onConfirm={handleSendNowSchedule}
            isOpened
            withButtons="YesNo"
            body={
              <Grid container justify="space-between" className={classes.dialogSections}>
                {scheduleSendNowDetails.map(({ title, detail }) => (
                  <Grid key={title} container item sm={6} direction="column" wrap="nowrap">
                    <Typography variant="subtitle1">{title}</Typography>
                    <When condition={!!detail}>
                      <Typography variant="body2">{detail}</Typography>
                    </When>
                  </Grid>
                ))}
              </Grid>
            }
          />
        </When>
        <When condition={uiState.isDeleteDraftDialogOpen}>
          <DecisionDialog
            {...dialogsCopies.draftDelete}
            fetching={bulkEmailStatus.loading}
            isOpened
            onClose={handleToggleBulkDialogs(false, BULK_DIALOGS.deleteDraft)}
            onConfirm={handleDeleteBulkDraft}
            withButtons="YesNo"
          />
        </When>
        <When condition={templatesUiState.isArchiveDialogOpen}>
          <DecisionDialog
            {...templateArchiveDialogProps}
            withButtons="YesNo"
            mustComplete
            fetching={templatesUiState.loading}
            onClose={templateTogglers.archiveDialog({ open: false })}
          />
        </When>
        <When condition={templatesUiState.isRestoreDrawerOpen}>
          <TemplateRestoreForm
            itemToRestore={templatesTreeItem}
            onClose={templateTogglers.restoreDrawer({ open: false })}
            onRestore={templateHandlers.restoreTemplateTreeItem}
          />
        </When>
        <When condition={uiState.singleStepDrawer.open}>
          <BulkEmailStepsDrawer
            bulkConfig={{
              id: bulkEmailPreviewId,
              searchProject: searchProjectInPreview,
              scope: bulkScope,
              candidates:
                bulkEmailInPreview.data?.emailConfig?.marketing?.marketingCandidates || null,
              jobOrders:
                bulkEmailInPreview.data?.emailConfig?.recruiting?.marketingCandidates || null
            }}
            onClose={toggleSequenceStepsDrawer({ open: false })}
            step={uiState.singleStepDrawer.step}
          />
        </When>
        <When condition={templatesUiState.isFolderDrawerOpen}>
          <TemplateFolderDrawer
            templatesTreeItem={templatesTreeItem}
            isFolderBeingEdited={templatesUiState.isFolderBeingEdited}
            onClose={templateTogglers.folderDrawer({ open: false, edit: false })}
            onSubmitFolder={templateHandlers.submitFolder}
          />
        </When>
        <When condition={templatesUiState.isTemplateDrawerOpen}>
          <BulkTemplatesDrawer
            isTemplateBeingEdited={templatesUiState.isTemplateBeingEdited}
            onCloseDrawer={handleOnCloseTemplateDrawer}
            onSubmitTemplate={templateHandlers.submitTemplate}
            templatesTreeItem={templatesTreeItem}
          />
          <DecisionDialog
            isOpened={templatesUiState.isDiscardTemplateDialogOpen}
            title={templateCopies.dialogs.discard.title}
            message={templateCopies.dialogs.discard.message}
            confirmButtonText={dialogsCopies.cancel.copies.discardButtonText}
            cancelButtonText={dialogsCopies.cancel.copies.keepEditingButtonText}
            isHighLightActionOnLeft
            withButtons="YesNo"
            onConfirm={closeTemplateDrawer}
            onClose={templateTogglers.discardTemplateDialog({ open: false })}
          />
        </When>
        <When condition={uiState.isStatsDrawerOpen}>
          <BulkStatsDrawer
            bulkId={bulkEmailPreviewId}
            bulkTypeId={bulkScope?.id}
            onClose={toggleStatsDrawer(false)}
            userEmail={userInfo?.email}
          />
        </When>
        <When condition={uiState[BULK_DIALOGS.cancelSequence]}>
          <DecisionDialog
            {...dialogsCopies.sequences.cancelOwnSequence}
            withButtons="YesNo"
            fetching={bulkEmailStatus.loading}
            onConfirm={ok => ok && handleCancelSequence()}
            onClose={handleToggleBulkDialogs(false, BULK_DIALOGS.cancelSequence)}
          />
        </When>
      </>
    )
  );
};

export default BulkEmailContent;
